import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ArchiveX,
    ArrowLeft,
    Bell,
    Check,
    CheckCheck,
    ChevronRight,
    Circle,
    Eraser,
    Flag,
    Inbox,
    Loader2,
    MessageCircle,
    NotebookTabs,
    RefreshCw,
    Search,
    Shield,
    SlidersHorizontal,
    Sparkles,
    Trash2,
    UserPlus,
    UserRound,
    X,
} from "lucide-react";
import FooterNav from "../components/FooterNav";
import {
    deleteNotification,
    deleteNotifications,
    fetchNotifications,
    markAllNotificationsRead,
    markNotificationsRead,
    markNotificationsUnread,
    type NotificationItem,
} from "../api/notifications";
import { acceptSpaceInviteFromAlert } from "../api/spaces";
import { connectSocket } from "../api/socket";
import { formatBlackspaceDateTime } from "../utils/time";
import { notificationPreferenceAllows, readNotificationPreferences } from "../utils/activeNotifications";

const NOTIFICATIONS_UPDATED_EVENT = "blackspace:notifications-updated";
const PAGE_SIZE = 24;

type AlertCategory = "all" | "messages" | "requests" | "social" | "spaces" | "vault" | "events" | "security";

const ALERT_CATEGORIES: Array<{ id: AlertCategory; label: string; icon: any; tone: string }> = [
    { id: "all", label: "All", icon: Bell, tone: "text-cyan-200" },
    { id: "messages", label: "Messages", icon: MessageCircle, tone: "text-cyan-200" },
    { id: "requests", label: "Requests", icon: UserPlus, tone: "text-amber-200" },
    { id: "social", label: "Replies", icon: Sparkles, tone: "text-blue-200" },
    { id: "spaces", label: "Spaces", icon: Flag, tone: "text-emerald-200" },
    { id: "vault", label: "Vault", icon: NotebookTabs, tone: "text-violet-200" },
    { id: "events", label: "Events", icon: Inbox, tone: "text-fuchsia-200" },
    { id: "security", label: "Security", icon: Shield, tone: "text-rose-200" },
];

function publishNotificationUpdate(unreadCount: number) {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, { detail: { unreadCount } }));
}

function isAcceptableSpaceInvite(item: NotificationItem) {
    return item.type === "space_invite"
        && item.metadata?.inviteAction === "accept"
        && typeof item.metadata?.spaceId === "string"
        && item.metadata.spaceId.trim().length > 0;
}

function formatTime(value: string) {
    return formatBlackspaceDateTime(value);
}

function typeLabel(type: string) {
    const labels: Record<string, string> = {
        message: "Message",
        message_request: "Message request",
        friend_request: "Follow request",
        comment: "Comment",
        reply: "Reply",
        note_shared: "Vault",
        space_report: "Space report",
        space_invite: "Space invite",
        space_post: "Space post",
        space_mention: "Mention",
        space_material: "Space material",
        space_post_removed: "Post restricted",
        space_post_deleted: "Post deleted",
        space_role: "Space role",
        space_restriction: "Restriction",
        event_created: "Event",
        event_updated: "Event update",
        event_cancelled: "Event cancelled",
        event_deleted: "Event deleted",
        system: "System",
        authority: "Security",
    };
    return labels[type] || type.replace(/_/g, " ");
}

function iconFor(type: string, className = "") {
    if (type === "friend_request") return <UserPlus size={17} className={className || "text-amber-300"} />;
    if (type === "message" || type === "message_request") return <MessageCircle size={17} className={className || "text-cyan-300"} />;
    if (type === "comment" || type === "reply") return <Sparkles size={17} className={className || "text-blue-300"} />;
    if (type === "note_shared") return <NotebookTabs size={17} className={className || "text-violet-300"} />;
    if (type.startsWith("space_")) return <Flag size={17} className={className || "text-emerald-300"} />;
    if (type === "authority" || type === "system") return <Shield size={17} className={className || "text-rose-300"} />;
    if (type.startsWith("event_")) return <Inbox size={17} className={className || "text-fuchsia-300"} />;
    return <Bell size={17} className={className || "text-neutral-300"} />;
}

function groupKey(item: NotificationItem) {
    const metadata = item.metadata || {};
    return [
        item.type,
        metadata.conversationId || "",
        metadata.spaceId || "",
        metadata.postId || "",
        metadata.noteId || "",
        item.href || "",
    ].join(":");
}

type AlertGroup = {
    key: string;
    primary: NotificationItem;
    items: NotificationItem[];
    ids: string[];
    unreadCount: number;
    actorLine: string;
};

function groupNotifications(items: NotificationItem[]): AlertGroup[] {
    const groups = new Map<string, NotificationItem[]>();
    for (const item of items) {
        const key = groupKey(item);
        const bucket = groups.get(key) || [];
        bucket.push(item);
        groups.set(key, bucket);
    }
    return Array.from(groups.entries()).map(([key, grouped]) => {
        const sorted = [...grouped].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const actors = Array.from(new Set(sorted.map((item) => item.actor?.displayName || item.actor?.username).filter(Boolean)));
        const actorLine = actors.length > 1
            ? `${actors[0]} and ${actors.length - 1} other${actors.length > 2 ? "s" : ""}`
            : actors[0] || typeLabel(sorted[0].type);
        return {
            key,
            primary: sorted[0],
            items: sorted,
            ids: sorted.map((item) => item.id),
            unreadCount: sorted.filter((item) => !item.readAt).length,
            actorLine,
        };
    });
}

function matchesLocalPreferences(item: NotificationItem, respectPreferences: boolean) {
    if (!respectPreferences) return true;
    return notificationPreferenceAllows(item, readNotificationPreferences());
}

export default function Alerts() {
    const navigate = useNavigate();
    const location = useLocation();
    const routeState = location.state as { from?: string; reopenBlackspaceMenuOnBack?: boolean } | null;
    const originRef = useRef<string | null>(routeState?.from || null);
    const reopenMenuOnBackRef = useRef(Boolean(routeState?.reopenBlackspaceMenuOnBack));
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const [items, setItems] = useState<NotificationItem[]>([]);
    const [queuedItems, setQueuedItems] = useState<NotificationItem[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const [category, setCategory] = useState<AlertCategory>("all");
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [respectPreferences, setRespectPreferences] = useState(true);
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedQuery(query.trim()), 240);
        return () => window.clearTimeout(timeout);
    }, [query]);

    const load = useCallback(async (reset = true) => {
        reset ? setLoading(true) : setLoadingMore(true);
        setError(null);
        try {
            const data = await fetchNotifications({
                cursor: reset ? null : nextCursor,
                limit: PAGE_SIZE,
                unread: showUnreadOnly,
                category,
                q: debouncedQuery,
            });
            setItems((current) => reset ? data.notifications : [...current, ...data.notifications]);
            setNextCursor(data.nextCursor);
            setHasMore(data.hasMore);
            setTotal(data.total);
            setUnreadCount(data.unreadCount);
            publishNotificationUpdate(data.unreadCount);
            if (reset) setQueuedItems([]);
        } catch (err: any) {
            setError(err?.message || "Could not load alerts.");
        } finally {
            reset ? setLoading(false) : setLoadingMore(false);
        }
    }, [category, debouncedQuery, nextCursor, showUnreadOnly]);

    useEffect(() => {
        setSelectedIds(new Set());
        load(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, debouncedQuery, showUnreadOnly]);

    useEffect(() => {
        if (!("IntersectionObserver" in window)) return;
        const node = sentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting) && hasMore && !loading && !loadingMore) {
                load(false);
            }
        }, { rootMargin: "280px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, load, loading, loadingMore]);

    useEffect(() => {
        if (!localStorage.getItem("token")) return;
        let socket: ReturnType<typeof connectSocket> | null = null;

        const handleNewNotification = (payload: { notification?: NotificationItem; unreadCount?: number }) => {
            if (typeof payload.unreadCount === "number") {
                setUnreadCount(payload.unreadCount);
                publishNotificationUpdate(payload.unreadCount);
            }
            const notification = payload.notification;
            if (!notification) return;
            if (!matchesLocalPreferences(notification, respectPreferences)) return;
            if (category !== "all" && !notificationMatchesCategory(notification, category)) return;
            if (showUnreadOnly && notification.readAt) return;
            if (debouncedQuery && !notificationMatchesSearch(notification, debouncedQuery)) return;
            setQueuedItems((current) => [notification, ...current.filter((item) => item.id !== notification.id)]);
        };

        const handleSummary = (payload: { unreadCount?: number }) => {
            if (typeof payload.unreadCount === "number") {
                setUnreadCount(payload.unreadCount);
                publishNotificationUpdate(payload.unreadCount);
            }
        };

        const handleConversationCleared = (payload: { conversationId?: string; unreadCount?: number }) => {
            if (payload.conversationId) {
                setItems((current) => current.filter((item) => String(item.metadata?.conversationId || "") !== String(payload.conversationId)));
                setQueuedItems((current) => current.filter((item) => String(item.metadata?.conversationId || "") !== String(payload.conversationId)));
            }
            if (typeof payload.unreadCount === "number") {
                setUnreadCount(payload.unreadCount);
                publishNotificationUpdate(payload.unreadCount);
            }
        };

        try {
            socket = connectSocket();
            socket.on("notification:new", handleNewNotification);
            socket.on("notification:summary", handleSummary);
            socket.on("notification:conversation-cleared", handleConversationCleared);
        } catch {
            return;
        }

        return () => {
            socket?.off("notification:new", handleNewNotification);
            socket?.off("notification:summary", handleSummary);
            socket?.off("notification:conversation-cleared", handleConversationCleared);
        };
    }, [category, debouncedQuery, respectPreferences, showUnreadOnly]);

    const visibleItems = useMemo(
        () => items.filter((item) => matchesLocalPreferences(item, respectPreferences)),
        [items, respectPreferences]
    );
    const groups = useMemo(() => groupNotifications(visibleItems), [visibleItems]);
    const selectedCount = selectedIds.size;
    const visibleAlertIds = useMemo(
        () => Array.from(new Set(groups.flatMap((group) => group.ids))),
        [groups]
    );
    const allVisibleSelected = visibleAlertIds.length > 0 && visibleAlertIds.every((id) => selectedIds.has(id));

    function mergeQueued() {
        setItems((current) => [
            ...queuedItems,
            ...current.filter((item) => !queuedItems.some((queued) => queued.id === item.id)),
        ]);
        setQueuedItems([]);
    }

    function toggleGroup(group: AlertGroup) {
        setSelectedIds((current) => {
            const next = new Set(current);
            const allSelected = group.ids.every((id) => next.has(id));
            group.ids.forEach((id) => allSelected ? next.delete(id) : next.add(id));
            return next;
        });
    }

    function toggleSelectAllVisible() {
        setSelectedIds((current) => {
            if (allVisibleSelected) return new Set();
            const next = new Set(current);
            visibleAlertIds.forEach((id) => next.add(id));
            return next;
        });
    }

    async function openGroup(group: AlertGroup) {
        if (selectedCount > 0) {
            toggleGroup(group);
            return;
        }
        const item = group.primary;
        if (isAcceptableSpaceInvite(item)) {
            const spaceId = String(item.metadata?.spaceId || "").trim();
            setBusy(true);
            setError(null);
            setNotice(null);
            try {
                const result = await acceptSpaceInviteFromAlert(spaceId, item.id);
                const readAt = result.notification?.readAt || new Date().toISOString();
                setItems((current) => current.map((entry) => (
                    group.ids.includes(entry.id)
                        ? {
                            ...entry,
                            readAt,
                            metadata: entry.id === item.id
                                ? { ...entry.metadata, ...(result.notification?.metadata || {}) }
                                : entry.metadata,
                        }
                        : entry
                )));
                setQueuedItems((current) => current.map((entry) => (
                    group.ids.includes(entry.id)
                        ? {
                            ...entry,
                            readAt,
                            metadata: entry.id === item.id
                                ? { ...entry.metadata, ...(result.notification?.metadata || {}) }
                                : entry.metadata,
                        }
                        : entry
                )));
                setSelectedIds(new Set());
                setUnreadCount(result.unreadCount);
                publishNotificationUpdate(result.unreadCount);
                setNotice(result.message || `Joined ${item.metadata?.spaceTitle || "Space"}.`);
                navigate(`/spaces/${encodeURIComponent(spaceId)}`, {
                    state: {
                        notificationType: item.type,
                        spaceInviteAccepted: true,
                        notificationFallback: { title: item.title, href: item.href },
                    },
                });
            } catch (err: any) {
                setError(err?.message || "This invitation is no longer available.");
            } finally {
                setBusy(false);
            }
            return;
        }
        const unreadIds = group.ids.filter((id) => items.some((entry) => entry.id === id && !entry.readAt));
        if (unreadIds.length) {
            await markRead(unreadIds, false);
        }
        if (!item.href) {
            setNotice("This alert has no destination yet.");
            return;
        }
        navigate(item.href, {
            state: {
                conversationId: item.metadata?.conversationId,
                notificationType: item.type,
                notificationFallback: { title: item.title, href: item.href },
            },
        });
    }

    async function markRead(ids: string[], showMessage = true) {
        const cleanIds = Array.from(new Set(ids));
        if (!cleanIds.length) return;
        const readAt = new Date().toISOString();
        setItems((current) => current.map((item) => cleanIds.includes(item.id) ? { ...item, readAt } : item));
        setSelectedIds(new Set());
        try {
            const result = await markNotificationsRead(cleanIds);
            setUnreadCount(result.unreadCount);
            publishNotificationUpdate(result.unreadCount);
            if (showMessage) setNotice("Marked as read.");
        } catch (err: any) {
            setError(err?.message || "Could not mark alerts read.");
            load(true);
        }
    }

    async function markUnread(ids: string[]) {
        const cleanIds = Array.from(new Set(ids));
        if (!cleanIds.length) return;
        setItems((current) => current.map((item) => cleanIds.includes(item.id) ? { ...item, readAt: null } : item));
        setSelectedIds(new Set());
        try {
            const result = await markNotificationsUnread(cleanIds);
            setUnreadCount(result.unreadCount);
            publishNotificationUpdate(result.unreadCount);
            setNotice("Kept unread.");
        } catch (err: any) {
            setError(err?.message || "Could not keep alerts unread.");
            load(true);
        }
    }

    async function removeAlerts(ids: string[]) {
        const cleanIds = Array.from(new Set(ids));
        if (!cleanIds.length) return;
        setBusy(true);
        setItems((current) => current.filter((item) => !cleanIds.includes(item.id)));
        setSelectedIds(new Set());
        try {
            const result = cleanIds.length === 1
                ? await deleteNotification(cleanIds[0]).then((response) => ({ ...response, ids: cleanIds }))
                : await deleteNotifications(cleanIds);
            setUnreadCount(result.unreadCount);
            publishNotificationUpdate(result.unreadCount);
            setNotice(cleanIds.length === 1 ? "Alert removed." : "Alerts removed.");
        } catch (err: any) {
            setError(err?.message || "Could not remove alerts.");
            load(true);
        } finally {
            setBusy(false);
        }
    }

    async function markAllRead() {
        setBusy(true);
        try {
            const result = await markAllNotificationsRead();
            setUnreadCount(result.unreadCount);
            publishNotificationUpdate(result.unreadCount);
            setItems((current) => current.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
            setQueuedItems([]);
            setNotice("All alerts marked read.");
        } catch (err: any) {
            setError(err?.message || "Could not mark alerts read.");
        } finally {
            setBusy(false);
        }
    }

    function goBack() {
        const currentRoute = `${location.pathname}${location.search}${location.hash}`;
        if (originRef.current && originRef.current !== currentRoute) {
            navigate(originRef.current, { state: reopenMenuOnBackRef.current ? { reopenBlackspaceMenu: true } : undefined });
            return;
        }
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate("/home");
    }

    return (
        <div className="bs-mobile-alerts flex h-screen flex-col overflow-hidden bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <main className="min-h-0 flex-1 overflow-y-auto px-4 pb-32 pt-5 sm:px-8">
                <div className="mx-auto max-w-5xl space-y-4">
                    <header className="bs-alerts-mobile-hero relative overflow-hidden rounded-[1.75rem] border border-cyan-400/15 bg-black/70 p-4 shadow-[0_0_90px_rgba(8,145,178,0.12)]">
                        <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_12%_0%,rgba(8,145,178,0.26),transparent_34%),linear-gradient(130deg,rgba(8,145,178,0.12),transparent_45%,rgba(168,85,247,0.10))]" />
                        <div className="relative flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="bs-alerts-title-row">
                                    <button type="button" onClick={goBack} className="bs-alerts-back-button md:hidden" aria-label="Go back">
                                        <ArrowLeft size={18} />
                                    </button>
                                    <h1 className="bs-message-page-title truncate">Alerts</h1>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <button type="button" onClick={() => setSearchOpen((value) => !value)} className="bs-premium-header-action grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(8,145,178,0.12)] hover:text-cyan-200" title="Search alerts">
                                    <Search size={20} />
                                </button>
                                <button type="button" onClick={() => setFiltersOpen((value) => !value)} className="bs-premium-header-action grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(168,85,247,0.10)] hover:text-white" title="Filter alerts">
                                    <SlidersHorizontal size={20} />
                                </button>
                                <button type="button" onClick={() => load(true)} disabled={loading} className="bs-premium-header-action grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(8,145,178,0.12)] hover:text-cyan-200 disabled:opacity-50" title="Refresh alerts">
                                    <RefreshCw size={19} className={loading ? "animate-spin" : ""} />
                                </button>
                                <button type="button" onClick={markAllRead} disabled={busy || unreadCount === 0} className="bs-premium-header-action hidden h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(8,145,178,0.12)] hover:text-cyan-200 disabled:cursor-not-allowed disabled:opacity-45 md:grid" title="Mark all read" aria-label="Mark all alerts read">
                                    <CheckCheck size={19} />
                                </button>
                                <button type="button" onClick={toggleSelectAllVisible} disabled={visibleAlertIds.length === 0} className={`bs-premium-header-action hidden h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] shadow-[0_0_30px_rgba(168,85,247,0.10)] disabled:cursor-not-allowed disabled:opacity-45 md:grid ${allVisibleSelected ? "text-cyan-100" : "text-neutral-200 hover:text-white"}`} title={allVisibleSelected ? "Clear selected alerts" : "Select all loaded alerts"} aria-label={allVisibleSelected ? "Clear selected alerts" : "Select all loaded alerts"} aria-pressed={allVisibleSelected}>
                                    {allVisibleSelected ? <Check size={19} /> : <Circle size={18} />}
                                </button>
                            </div>
                        </div>

                        {searchOpen && (
                            <div className="relative mt-4">
                                <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-300" />
                                <input
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search alerts, people, spaces"
                                    className="h-12 w-full rounded-2xl border border-white/10 bg-black/70 pl-11 pr-11 text-sm font-bold text-white outline-none focus:border-cyan-700"
                                />
                                {query && (
                                    <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white" title="Clear search">
                                        <X size={17} />
                                    </button>
                                )}
                            </div>
                        )}

                        {filtersOpen && (
                            <div className="relative mt-4 space-y-3 rounded-2xl bg-white/[0.035] p-3">
                                <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    {ALERT_CATEGORIES.map((filter) => {
                                        const Icon = filter.icon;
                                        const active = category === filter.id;
                                        return (
                                            <button
                                                key={filter.id}
                                                type="button"
                                                onClick={() => setCategory(filter.id)}
                                                className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-xl px-3 text-xs font-black transition ${active ? "bg-cyan-400/12 text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,0.18)]" : "bg-black/45 text-neutral-500 hover:text-neutral-200"}`}
                                            >
                                                <Icon size={15} className={active ? "text-cyan-200" : filter.tone} />
                                                {filter.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowUnreadOnly((value) => !value)}
                                        className={`h-10 rounded-xl text-xs font-black transition ${showUnreadOnly ? "bg-amber-400/12 text-amber-100" : "bg-black/45 text-neutral-500 hover:text-neutral-200"}`}
                                    >
                                        Unread only
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRespectPreferences((value) => !value)}
                                        className={`h-10 rounded-xl text-xs font-black transition ${respectPreferences ? "bg-emerald-400/12 text-emerald-100" : "bg-black/45 text-neutral-500 hover:text-neutral-200"}`}
                                    >
                                        Muted hidden
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="bs-alerts-stat-row relative mt-4 flex flex-wrap items-center gap-2 text-xs font-bold text-neutral-500">
                            <span>{unreadCount} unread</span>
                            <span>·</span>
                            <span>{total} total</span>
                            <span>·</span>
                            <span>{visibleItems.length} loaded</span>
                        </div>
                    </header>

                    {queuedItems.length > 0 && (
                        <button
                            type="button"
                            onClick={mergeQueued}
                            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cyan-400/12 px-4 py-3 text-sm font-black text-cyan-100 shadow-[0_0_28px_rgba(8,145,178,0.12)]"
                        >
                            <Sparkles size={16} />
                            {queuedItems.length} new alert{queuedItems.length === 1 ? "" : "s"}
                        </button>
                    )}

                    {notice && (
                        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-950/25 px-4 py-3 text-sm font-bold text-emerald-100">
                            {notice}
                        </div>
                    )}
                    {error && (
                        <div role="alert" className="rounded-2xl border border-red-400/20 bg-red-950/25 px-4 py-3 text-sm font-bold text-red-100">
                            {error}
                        </div>
                    )}

                    {selectedCount > 0 && (
                        <div className="sticky top-3 z-20 flex items-center gap-2 rounded-3xl border border-cyan-400/20 bg-black/90 p-2 shadow-[0_0_44px_rgba(8,145,178,0.16)] backdrop-blur-xl">
                            <span className="mr-auto rounded-2xl bg-white/[0.04] px-4 py-2 text-sm font-black text-white">
                                {selectedCount} selected
                            </span>
                            <button type="button" onClick={() => markRead(Array.from(selectedIds))} className="grid h-11 w-11 place-items-center rounded-2xl bg-cyan-400/10 text-cyan-100" title="Mark read">
                                <CheckCheck size={18} />
                            </button>
                            <button type="button" onClick={() => markUnread(Array.from(selectedIds))} className="grid h-11 w-11 place-items-center rounded-2xl bg-amber-400/10 text-amber-100" title="Keep unread">
                                <Circle size={17} />
                            </button>
                            <button type="button" onClick={() => removeAlerts(Array.from(selectedIds))} disabled={busy} className="grid h-11 w-11 place-items-center rounded-2xl bg-red-500/10 text-red-100 disabled:opacity-50" title="Delete">
                                {busy ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                            </button>
                            <button type="button" onClick={() => setSelectedIds(new Set())} className="grid h-11 w-11 place-items-center rounded-2xl bg-white/[0.04] text-neutral-400" title="Clear selection">
                                <X size={18} />
                            </button>
                        </div>
                    )}

                    <section className="space-y-3">
                        {groups.map((group) => {
                            const item = group.primary;
                            const groupSelected = group.ids.every((id) => selectedIds.has(id));
                            const unread = group.unreadCount > 0;
                            const countLabel = group.items.length > 1 ? `${group.items.length} alerts` : typeLabel(item.type);
                            return (
                                <article
                                    key={group.key}
                                    className={`group relative overflow-hidden rounded-3xl bg-white/[0.035] p-3 transition hover:bg-white/[0.055] ${unread ? "shadow-[inset_0_0_0_1px_rgba(34,211,238,0.22),0_0_34px_rgba(8,145,178,0.10)]" : "shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)]"}`}
                                >
                                    <div className="pointer-events-none absolute inset-0 opacity-0 transition group-hover:opacity-100 [background:radial-gradient(circle_at_15%_0%,rgba(8,145,178,0.16),transparent_32%)]" />
                                    <div className="relative flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(group)}
                                            className={`mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-2xl transition ${groupSelected ? "bg-cyan-400/15 text-cyan-100" : "bg-black/45 text-neutral-600 hover:text-cyan-200"}`}
                                            title={groupSelected ? "Unselect" : "Select"}
                                        >
                                            {groupSelected ? <Check size={17} /> : <Circle size={14} />}
                                        </button>

                                        <button type="button" onClick={() => openGroup(group)} className="min-w-0 flex-1 text-left">
                                            <div className="flex items-start gap-3">
                                                <div className="relative grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-black/55 text-sm font-black text-cyan-100">
                                                    {item.actor?.avatarUrl ? (
                                                        <img src={item.actor.avatarUrl} alt="" className="h-full w-full rounded-2xl object-cover" />
                                                    ) : (
                                                        <UserRound size={18} />
                                                    )}
                                                    <span className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-xl bg-neutral-950 shadow-[0_0_18px_rgba(8,145,178,0.16)]">
                                                        {iconFor(item.type)}
                                                    </span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <p className="truncate text-sm font-black text-white">{group.items.length > 1 ? group.actorLine : item.title}</p>
                                                            <p className="mt-1 line-clamp-2 text-sm leading-5 text-neutral-400">
                                                                {group.items.length > 1 ? `${countLabel} · ${item.title}` : item.body || "Open alert"}
                                                            </p>
                                                        </div>
                                                        <span className="shrink-0 text-[11px] font-bold text-neutral-500">{formatTime(item.createdAt)}</span>
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-bold text-neutral-500">
                                                        <span className="rounded-full bg-black/45 px-2 py-1">{countLabel}</span>
                                                        {unread && <span className="rounded-full bg-cyan-400/10 px-2 py-1 text-cyan-200">{group.unreadCount} unread</span>}
                                                        {(item.actor?.username || item.actor?.displayName) && group.items.length === 1 && (
                                                            <span className="rounded-full bg-black/45 px-2 py-1">{item.actor.displayName || `@${item.actor.username}`}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>

                                        <div className="relative flex shrink-0 flex-col gap-2">
                                            <button type="button" onClick={() => removeAlerts(group.ids)} className="grid h-9 w-9 place-items-center rounded-2xl bg-red-500/10 text-red-100 opacity-80 transition hover:opacity-100" title="Delete alert">
                                                <Trash2 size={15} />
                                            </button>
                                            <button type="button" onClick={() => openGroup(group)} className="grid h-9 w-9 place-items-center rounded-2xl bg-white/[0.04] text-neutral-300 transition hover:text-cyan-100" title="Open alert">
                                                <ChevronRight size={17} />
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}

                        {!loading && groups.length === 0 && (
                            <div className="bs-alerts-empty-card relative overflow-hidden rounded-3xl bg-white/[0.035] p-10 text-center shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]">
                                <div className="pointer-events-none absolute inset-0 [background:radial-gradient(circle_at_50%_0%,rgba(244,63,94,0.18),transparent_36%)]" />
                                <ArchiveX size={32} className="relative mx-auto mb-3 text-neutral-600" />
                                <h2 className="relative text-lg font-black text-white">No alerts here</h2>
                                <p className="relative mx-auto mt-2 max-w-sm text-sm leading-6 text-neutral-500">
                                    Messages, follow requests, Vault shares, Space actions, and security notes will appear here.
                                </p>
                            </div>
                        )}

                        {loading && items.length === 0 && (
                            <div className="space-y-3">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={index} className="h-24 animate-pulse rounded-3xl bg-white/[0.035]" />
                                ))}
                            </div>
                        )}

                        <div ref={sentinelRef} className="h-4" />

                        {loadingMore && (
                            <div className="flex justify-center py-3 text-cyan-200">
                                <Loader2 size={20} className="animate-spin" />
                            </div>
                        )}

                        {!loading && hasMore && !loadingMore && (
                            <button type="button" onClick={() => load(false)} className="mx-auto flex h-11 items-center gap-2 rounded-2xl bg-cyan-400/10 px-5 text-sm font-black text-cyan-100">
                                Show more
                            </button>
                        )}

                        {items.length > 0 && (
                            <div className="flex justify-center gap-2 pt-2">
                                <button type="button" onClick={markAllRead} disabled={busy || unreadCount === 0} className="inline-flex h-10 items-center gap-2 rounded-2xl bg-white/[0.04] px-4 text-xs font-black text-neutral-300 hover:text-cyan-100 disabled:opacity-50">
                                    <Eraser size={15} />
                                    Mark all read
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            </main>
            <FooterNav />
        </div>
    );
}

function notificationMatchesCategory(item: NotificationItem, category: AlertCategory) {
    if (category === "all") return true;
    if (category === "messages") return item.type === "message" || item.type === "message_request";
    if (category === "requests") return item.type === "friend_request" || item.type === "space_invite";
    if (category === "social") return item.type === "comment" || item.type === "reply";
    if (category === "vault") return item.type === "note_shared";
    if (category === "spaces") return item.type.startsWith("space_");
    if (category === "events") return item.type.startsWith("event_");
    if (category === "security") return item.type === "system" || item.type === "authority";
    return true;
}

function notificationMatchesSearch(item: NotificationItem, query: string) {
    const haystack = [
        item.title,
        item.body,
        item.type,
        item.actor?.username,
        item.actor?.displayName,
    ].join(" ").toLowerCase();
    return haystack.includes(query.toLowerCase());
}
